// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TerceraWindowBase } from './TerceraWindowBase';
import OptionCalculatorScreenTemplate from '@front/templates/OptionCalculatorScreenTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { TerceraButtonInfo } from '../elements/TerceraGroupButtons';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { GreeksFormatter } from '@shared/commons/cache/OptionMaster/OptionTrader/GreeksFormatter';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
export class OptionCalculatorScreen extends TerceraWindowBase {
    constructor() {
        super();
    }
    oninit() {
        super.oninit();
        this.on('inputParametersGroupButtons_btnClick', this.inputParametersGroupButtons_btnClicked);
        this.on('calculateBtnClick', this.onCalculateBtnClicked);
        this.on('resetBtnClick', this.onResetBtnClicked);
    }
    onteardown() {
        this.off('inputParametersGroupButtons_btnClick', this.inputParametersGroupButtons_btnClicked);
        this.off('calculateBtnClick', this.onCalculateBtnClicked);
        this.off('resetBtnClick', this.onResetBtnClicked);
        super.onteardown();
    }
    localize() {
        super.localize();
        void this.set({
            header: Resources.getResource('screen.OptionCalculator'),
            inputParametersLabel: Resources.getResource('property.InputParameters'),
            outputParametersLabel: Resources.getResource('property.OutputParameters'),
            impliedVolatilityInputLabel: Resources.getResource('property.ImpliedVolatility'),
            theoreticalPriceInputLabel: Resources.getResource('property.TheoreticalOptionPrice'),
            strikePriceLabel: Resources.getResource('property.StrikePrice'),
            underlyingPriceLabel: Resources.getResource('property.UnderlyingPrice'),
            impliedVolatilityLabel: Resources.getResource('property.ImpliedVolatilityPercent'),
            optionPriceLabel: Resources.getResource('property.OptionPrice'),
            interestRateLabel: Resources.getResource('property.InterestRate'),
            daysTillExpirationLabel: Resources.getResource('property.DaysTillExpiration'),
            calculateButtonText: Resources.getResource('property.Calculate'),
            resetButtonText: 'C',
            resetButtonText_tt: Resources.getResource('property.Clear_tt'),
            putLabel: Resources.getResource('property.Put'),
            callLabel: Resources.getResource('property.Call'),
            deltaLabel: Resources.getResource('property.Delta'),
            gammaLabel: Resources.getResource('property.Gamma'),
            vegaLabel: Resources.getResource('property.Vega'),
            thetaLabel: Resources.getResource('property.Theta'),
            rhoLabel: Resources.getResource('property.Rho'),
            theoreticalPriceLabel: Resources.getResource('property.TheoreticalPrice'),
            OP_IV_tt: Resources.getResource('property.OP_IV_tt')
        });
    }
    close() {
        super.close();
        OptionCalculatorScreen._screen = null;
        OptionCalculatorScreen._optionCalculator = null;
    }
    static Show(optionCalculator) {
        if (!isNullOrUndefined(OptionCalculatorScreen._screen)) {
            OptionCalculatorScreen._screen.setFocus();
            return;
        }
        const screen = new OptionCalculatorScreen();
        screen.oninit();
        screen.localize();
        OptionCalculatorScreen._screen = screen;
        OptionCalculatorScreen._optionCalculator = optionCalculator;
        MainWindowManager.AddControl(OptionCalculatorScreen._screen);
        screen.center();
    }
    inputParametersGroupButtons_btnClicked(context, sender, buttonInfo) {
        void super.set('isIVChecked', buttonInfo.name === 'IV');
    }
    onCalculateBtnClicked() {
        const optionCalculator = OptionCalculatorScreen._optionCalculator;
        if (isNullOrUndefined(optionCalculator)) {
            this.onResetBtnClicked();
            return;
        }
        const isIVChecked = this.get('isIVChecked');
        let theoreticalPricePut = 0.0;
        let theoreticalPriceCall = 0.0;
        let impliedVolatilityPut = 0.0;
        let impliedVolatilityCall = 0.0;
        let deltaPut = 0.0;
        let deltaCall = 0.0;
        let gammaPut = 0.0;
        let gammaCall = 0.0;
        let vegaPut = 0.0;
        let vegaCall = 0.0;
        let thetaPut = 0.0;
        let thetaCall = 0.0;
        let rhoPut = 0.0;
        let rhoCall = 0.0;
        const interestRate = this.get('interestRate') / 100;
        const expiration = this.get('daysTillExpiration') / 365;
        if (isIVChecked) {
            theoreticalPricePut = theoreticalPriceCall = this.get('optionPrice');
            impliedVolatilityPut = optionCalculator.iv(OptionPutCall.OPTION_PUT_VANILLA, this.get('optionPrice'), this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate);
            impliedVolatilityCall = optionCalculator.iv(OptionPutCall.OPTION_CALL_VANILLA, this.get('optionPrice'), this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate);
        }
        else {
            impliedVolatilityPut = impliedVolatilityCall = this.get('impliedVolatility') / 100;
            theoreticalPricePut = optionCalculator.theoreticalPrice(OptionPutCall.OPTION_PUT_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
            theoreticalPriceCall = optionCalculator.theoreticalPrice(OptionPutCall.OPTION_CALL_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        }
        deltaPut = optionCalculator.delta(OptionPutCall.OPTION_PUT_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
        deltaCall = optionCalculator.delta(OptionPutCall.OPTION_CALL_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        gammaPut = optionCalculator.gamma(this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
        gammaCall = optionCalculator.gamma(this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        vegaPut = optionCalculator.vega(this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
        vegaCall = optionCalculator.vega(this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        thetaPut = optionCalculator.theta(OptionPutCall.OPTION_PUT_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
        thetaCall = optionCalculator.theta(OptionPutCall.OPTION_CALL_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        rhoPut = optionCalculator.rho(OptionPutCall.OPTION_PUT_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityPut);
        rhoCall = optionCalculator.rho(OptionPutCall.OPTION_CALL_VANILLA, this.get('underlyingPrice'), this.get('strikePrice'), expiration, interestRate, impliedVolatilityCall);
        const results = [
            deltaPut,
            deltaCall,
            gammaPut,
            gammaCall,
            vegaPut,
            vegaCall,
            thetaPut,
            thetaCall,
            rhoPut,
            rhoCall,
            impliedVolatilityPut,
            impliedVolatilityCall,
            theoreticalPricePut,
            theoreticalPriceCall
        ];
        const isEmpty = results.some((result) => !isValidNumber(result));
        void this.set({
            putDelta: isEmpty ? '' : GreeksFormatter.formatGreek(deltaPut, 5, true),
            callDelta: isEmpty ? '' : GreeksFormatter.formatGreek(deltaCall, 5, true),
            putGamma: isEmpty ? '' : GreeksFormatter.formatGreek(gammaPut, 5, true),
            callGamma: isEmpty ? '' : GreeksFormatter.formatGreek(gammaCall, 5, true),
            putVega: isEmpty ? '' : GreeksFormatter.formatGreek(vegaPut, 5, true),
            callVega: isEmpty ? '' : GreeksFormatter.formatGreek(vegaCall, 5, true),
            putTheta: isEmpty ? '' : GreeksFormatter.formatGreek(thetaPut, 5, true),
            callTheta: isEmpty ? '' : GreeksFormatter.formatGreek(thetaCall, 5, true),
            putRho: isEmpty ? '' : GreeksFormatter.formatGreek(rhoPut, 5, true),
            callRho: isEmpty ? '' : GreeksFormatter.formatGreek(rhoCall, 5, true),
            putImpliedVolatility: isEmpty ? '' : GreeksFormatter.formatIV(impliedVolatilityPut * 100, 5, true),
            callImpliedVolatility: isEmpty ? '' : GreeksFormatter.formatIV(impliedVolatilityCall * 100, 5, true),
            putTheoreticalPrice: isEmpty ? '' : PriceFormatter.formatPriceWithoutZeroes(theoreticalPricePut, 5, true),
            callTheoreticalPrice: isEmpty ? '' : PriceFormatter.formatPriceWithoutZeroes(theoreticalPriceCall, 5, true)
        });
    }
    onResetBtnClicked() {
        void this.set({
            putDelta: '',
            callDelta: '',
            putGamma: '',
            callGamma: '',
            putVega: '',
            callVega: '',
            putImpliedVolatility: '',
            callImpliedVolatility: '',
            putTheta: '',
            callTheta: '',
            putRho: '',
            callRho: '',
            putTheoreticalPrice: '',
            callTheoreticalPrice: ''
        });
    }
}
TerceraWindowBase.extendWith(OptionCalculatorScreen, {
    data: function () {
        return {
            showFooter: false,
            resizable: false,
            isIVChecked: false,
            inputParametersLabel: '',
            outputParametersLabel: '',
            impliedVolatilityInputLabel: '',
            theoreticalPriceInputLabel: '',
            strikePriceLabel: '',
            underlyingPriceLabel: '',
            impliedVolatilityLabel: '',
            optionPriceLabel: '',
            interestRateLabel: '',
            daysTillExpirationLabel: '',
            calculateButtonText: '',
            resetButtonText: '',
            resetButtonText_tt: '',
            putLabel: '',
            callLabel: '',
            deltaLabel: '',
            putDelta: '',
            callDelta: '',
            gammaLabel: '',
            putGamma: '',
            callGamma: '',
            vegaLabel: '',
            putVega: '',
            callVega: '',
            putImpliedVolatility: '',
            callImpliedVolatility: '',
            thetaLabel: '',
            putTheta: '',
            callTheta: '',
            rhoLabel: '',
            putRho: '',
            callRho: '',
            theoreticalPriceLabel: '',
            putTheoreticalPrice: '',
            callTheoreticalPrice: '',
            OP_IV_tt: '',
            inputParametersGroupButtonsInfo: [
                new TerceraButtonInfo('OP', 'OP', true),
                new TerceraButtonInfo('IV', 'IV', false)
            ],
            strikePrice: 0.00001,
            underlyingPrice: 0.00001,
            impliedVolatility: 0.00001,
            optionPrice: 0.00001,
            interestRate: 0.00001,
            daysTillExpiration: 1
        };
    },
    partials: { bodyPartial: OptionCalculatorScreenTemplate }
});
